import { useEffect, useMemo, useState } from 'react';
import { Search, X, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button, Input, Card, CardContent, Badge, Checkbox, Dialog, cn } from '../../ui';
import {
  getCategorias, createCategoria, updateCategoria, deleteCategoria,
} from '../api';
import { handleAuthError } from '../../../../funciones/authHelper';

export default function LaeCategorias() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]           = useState('');
  const [drawer, setDrawer] = useState(null);   // { id?, nombre, orden, activa }
  const [saving, setSaving] = useState(false);
  const [errForm, setErrForm] = useState('');
  const [delItem, setDelItem] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data } = await getCategorias();
      setItems(data);
    } catch (e) {
      if (handleAuthError) handleAuthError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  const filtradas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? items.filter((c) => c.nombre.toLowerCase().includes(t)) : items;
  }, [items, q]);

  const abrirNueva = () => { setErrForm(''); setDrawer({ nombre: '', orden: 0, activa: true }); };
  const abrirEdit  = (c) => { setErrForm(''); setDrawer({ id: c.id, nombre: c.nombre, orden: c.orden, activa: !!c.activa }); };

  const guardar = async () => {
    if (!drawer.nombre.trim()) { setErrForm('El nombre es obligatorio.'); return; }
    setSaving(true); setErrForm('');
    try {
      if (drawer.id) await updateCategoria(drawer.id, drawer);
      else           await createCategoria(drawer);
      setDrawer(null);
      await cargar();
    } catch (e) {
      setErrForm(e?.response?.data?.error || 'No se pudo guardar la categoría.');
      if (handleAuthError) handleAuthError(e);
    } finally {
      setSaving(false);
    }
  };

  const confirmarBorrado = async () => {
    try {
      await deleteCategoria(delItem.id);
      setDelItem(null);
      await cargar();
    } catch (e) {
      if (handleAuthError) handleAuthError(e);
    }
  };

  const total = items.length;
  const activas = items.filter((c) => c.activa).length;

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full">

      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">Categorías</h1>
          <p className="text-sm text-slate-500 mt-1">
            LAE Formación · <span className="font-medium text-slate-700">{total} categorías</span>
            {total > 0 && <> · {activas} activas</>}
          </p>
        </div>
        <Button onClick={abrirNueva}>
          <Plus className="w-4 h-4 mr-1.5" /> Crear categoría
        </Button>
      </div>

      {/* Filtros */}
      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="max-w-sm">
            <Input
              prefix={<Search className="w-4 h-4" />}
              suffix={q ? <button onClick={() => setQ('')}><X className="w-4 h-4" /></button> : null}
              placeholder="Buscar categoría…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide text-left">
              <tr>
                <th className="px-4 py-2.5">Nombre</th>
                <th className="px-4 py-2.5 w-24 text-center">Orden</th>
                <th className="px-4 py-2.5 w-32">Estado</th>
                <th className="px-4 py-2.5 w-24 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>
              )}

              {!loading && filtradas.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">
                  {q ? 'Sin resultados para la búsqueda.' : 'Aún no hay categorías. Crea la primera.'}
                </td></tr>
              )}

              {!loading && filtradas.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => abrirEdit(c)}
                >
                  <td className="px-4 py-2.5 font-medium text-slate-800">{c.nombre}</td>
                  <td className="px-4 py-2.5 text-center text-slate-500">{c.orden}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant={c.activa ? 'success' : 'secondary'}>
                      {c.activa ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="text-slate-400 hover:text-slate-700 mr-2"
                      title="Editar"
                      onClick={() => abrirEdit(c)}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      className="text-slate-400 hover:text-rose-500"
                      title="Eliminar"
                      onClick={() => setDelItem(c)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Drawer alta / edición */}
      {drawer && (
        <>
          <div className="fixed inset-0 bg-black/25 z-40" onClick={() => setDrawer(null)} />
          <aside className="fixed top-0 right-0 h-full w-[580px] max-w-[90vw] bg-white shadow-2xl z-50 flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">
                {drawer.id ? 'Editar categoría' : 'Nueva categoría'}
              </h2>
              <button className="text-slate-400 hover:text-slate-700" onClick={() => setDrawer(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre</label>
                <Input
                  autoFocus
                  placeholder="Ej. Idiomas"
                  value={drawer.nombre}
                  onChange={(e) => setDrawer({ ...drawer, nombre: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
                />
              </div>

              <div className="max-w-[160px]">
                <label className="block text-sm font-medium text-slate-700 mb-1">Orden</label>
                <Input
                  type="number"
                  min={0}
                  value={drawer.orden}
                  onChange={(e) => setDrawer({ ...drawer, orden: e.target.value })}
                />
                <p className="text-xs text-slate-400 mt-1">Menor número, aparece antes.</p>
              </div>

              <label className="flex items-center gap-2.5 cursor-pointer">
                <Checkbox
                  checked={drawer.activa}
                  onChange={(e) => setDrawer({ ...drawer, activa: e.target.checked })}
                />
                <span className="text-sm text-slate-700">Activa (visible en la web)</span>
              </label>

              {errForm && <p className="text-sm text-rose-600">{errForm}</p>}
            </div>

            <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDrawer(null)}>Cancelar</Button>
              <Button onClick={guardar} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </Button>
            </div>
          </aside>
        </>
      )}

      {/* Confirmación de borrado */}
      <Dialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        title="Eliminar categoría"
        description={delItem ? `¿Seguro que quieres eliminar "${delItem.nombre}"? Esta acción no se puede deshacer.` : ''}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDelItem(null)}>Cancelar</Button>
            <Button variant="danger" onClick={confirmarBorrado}>Eliminar</Button>
          </div>
        }
      />
    </div>
  );
}
